import type { IngestRun } from "@models/IngestRun.model";

/*
    What the Files page can do with the runs it has (MPN-1675). Pure functions so the
    rules are testable without rendering the panel.
*/

/** Runs "Process pending files" would pick up: uploaded, or validated and waiting to run. */
export const isPending = (run: IngestRun) => run.status === "uploaded" || run.status === "validated";

export const hasPendingRuns = (runs: readonly IngestRun[]) => runs.some(isPending);

/** Runs "Reprocess all files" replays: loaded, or validated and never loaded (MPN-1764). */
export const hasRunnableRuns = (runs: readonly IngestRun[]) =>
  runs.some((r) => r.status === "completed" || r.status === "validated");

/** Latest file date among COMPLETED runs (YYYY-MM-DD sorts as text), or null. */
export const latestCompletedAsOf = (runs: readonly IngestRun[]): string | null =>
  runs.reduce<string | null>(
    (latest, r) => (r.status === "completed" && r.as_of && (!latest || r.as_of > latest) ? r.as_of : latest),
    null,
  );

/** A pending run dated BEFORE the latest loaded file cannot be run on its own: the
 *  forward-only guard refuses it (loading it out of order would corrupt the conversion
 *  log). It runs through "Reprocess all files", which rebuilds from every stored file in
 *  order (MPN-1764) — that is the replace-a-file path: discard the old run, upload the
 *  corrected file, rebuild. The row hides Run and says so. */
export const needsRebuild = (run: IngestRun, runs: readonly IngestRun[]) => {
  if (!isPending(run) || !run.as_of) return false;
  const latest = latestCompletedAsOf(runs);
  return latest !== null && run.as_of < latest;
};

/** Completed, but the load wrote something worth a second look: a contact column that
 *  produced no contact points, or booleans read as no only because they matched nothing
 *  (MPN-1969). There is no separate status; the run is COMPLETED either way. */
export const hasLoadWarnings = (run: IngestRun) =>
  run.status === "completed" && (run.load_summary?.warnings.length ?? 0) > 0;
